import React, { useState, useEffect } from "react";
import { Modal, Select } from "antd";
import "../App.css";
import { toast } from "react-toastify";
import AddSubUser from "../components/AddSubUser";
import { useSelector } from "react-redux";
import api from "../Interceptor";
import { getRoute } from "../api/routes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CustomSelect } from "../components/ui";
import CustomModal from "../components/customModal/customModal";
import ModalFooter from "../components/customModal/modalFooter";
import useModal from "../hooks/useModal";
import { PlusOutlined } from "@ant-design/icons";
import { faPlus, faRefresh } from "@fortawesome/free-solid-svg-icons";
import { IoMdMore } from "react-icons/io";

const { Option } = Select;

const Team = () => {
  const user = useSelector((state) => state.auth.user);
  const [users, setUsers] = useState([]);
  const { projectList } = useSelector((state) => state.app);
  const [loading, setLoading] = useState(true);
  const [addUserModalVisible, setAddUserModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedAccess, setSelectedAccess] = useState(`All`);
  const [selectedStatus, setSelectedStatus] = useState(`All`);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserModalVisible, setEditUserModalVisible] = useState(false);
  const [editProject, setEditProject] = useState([]);
  const [editAccess, setEditAccess] = useState("");
  const [milestones, setMilestones] = useState([]);
  const [milestoneList, setMilestoneList] = useState([]);
  const { isOpen, toggleModal } = useModal();

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `${getRoute("users", user.userRole.name, "users", false, false, "")}`,
      );
      setUsers(response.data);
    } catch (error) {
      if (error?.isUnpaid) {
        return;
      }
      toast.error("Oops! Error while fetching Members");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const handleAiRefreshMembers = () => {
      fetchUsers();
    };
    window.addEventListener("ai:refreshMembers", handleAiRefreshMembers);
    return () => {
      window.removeEventListener("ai:refreshMembers", handleAiRefreshMembers);
    };
  }, []);

  const handleAddUser = () => {
    setAddUserModalVisible(true);
  };

  const handleModalClose = () => {
    setAddUserModalVisible(false);
  };

  const handleSuccess = () => {
    setAddUserModalVisible(false);
    fetchUsers();
  };

  const handleMilestoneChange = (value) => setMilestones(value);

  const handleAccessChange = (value) => setSelectedAccess(value);

  const handleEditProjectChange = async (selectedProjects) => {
    setEditProject(selectedProjects);

    if (!selectedProjects.length) {
      setMilestones([]);
      setMilestoneList([]);
      return;
    }

    const removedProject = editProject.filter(
      (p) => !selectedProjects.includes(p),
    );
    const addedProject = selectedProjects.filter(
      (p) => !editProject.includes(p),
    );
    if (removedProject.length > 0) {
      setMilestoneList((prev) =>
        prev.filter((m) => !removedProject.includes(m.project?._id)),
      );
      setMilestones((prev) => {
        const removedMilestoneIds = milestoneList
          .filter((m) => removedProject.includes(m.project?._id))
          .map((m) => m._id);
        return prev.filter((mileId) => !removedMilestoneIds.includes(mileId));
      });
    }
    if (addedProject.length > 0) {
      for (const projectId of addedProject) {
        await fetchMilestones(projectId);
      }
    }
  };

  const handleEditAccessChange = (value) => setEditAccess(value);
  const handleStatusChange = (value) => setSelectedStatus(value);

  const filteredUsers = users.filter((user) => {
    const matchesProject =
      selectedProject && selectedProject !== "All"
        ? user.projects.some((project) => project._id === selectedProject)
        : true;

    const matchesAccess =
      selectedAccess && selectedAccess !== "All"
        ? user.access._id === selectedAccess
        : true;

    const matchesStatus =
      selectedStatus && selectedStatus !== "All"
        ? user.status._id === selectedStatus
        : true;

    return matchesProject && matchesAccess && matchesStatus;
  });

  const updateStatus = async (userId, currentStatus) => {
    try {
      const newStatus =
        currentStatus === process.env.REACT_APP_STATUS_ACTIVE
          ? process.env.REACT_APP_STATUS_INACTIVE
          : process.env.REACT_APP_STATUS_ACTIVE;

      const response = await api.patch(
        getRoute("users", "admin", "users", false, false, "updateStatus"),
        {
          userId,
          statusId: newStatus,
        },
      );

      if (response.status === 200) {
        toast.success("User status updated successfully!");
        fetchUsers();
      }
    } catch (error) {
      toast.error("Failed to update status.");
      console.error("Error updating status:", error);
    }
  };

  const handleRefresh = () => {
    setSelectedStatus("All");
    setSelectedProject("All");
    setSelectedAccess("All");
    setMilestoneList([]);
  };

  const fetchMilestones = async (projects) => {
    try {
      const projectIds = Array.isArray(projects) ? projects : [projects];

      const response = await api.get(
        `${getRoute(
          "users",
          "admin",
          "projects",
          false,
          false,
          "milestone",
        )}?projectIds=${projectIds.join(",")}`,
      );

      setMilestoneList((prev) => {
        const merged = [...prev, ...response.data];
        const unique = [];
        const seen = new Set();

        for (const m of merged) {
          if (!seen.has(m._id)) {
            unique.push(m);
            seen.add(m._id);
          }
        }
        return unique;
      });

      return response.data;
    } catch (error) {
      toast.error("Error fetching milestones");
      return null;
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    const projects = [];

    const invalidProject = user.projects.some((project) => !project._id);

    if (!invalidProject) {
      projects.push(...user.projects.map((project) => project._id));
    }
    setEditProject(projects);

    const milestones = user.projects.flatMap((project) =>
      (project.milestones || [])
        .filter((m) => m && m._id)
        .map((milestone) => milestone?._id),
    );
    setMilestones(milestones);

    if (projects.length > 0) {
      fetchMilestones(projects);
    }

    setEditAccess(user.access._id);
    setEditUserModalVisible(true);
  };

  const handleUpdateUser = async () => {
    if (!editAccess) {
      toast.error("Access level is required!");
      return;
    }
    const originalMilestones = selectedUser.projects.flatMap((project) =>
      project.milestones.map((milestone) => milestone?._id),
    );

    const originalAccess = selectedUser.access._id;

    const milestonesUnchanged =
      originalMilestones.length === milestones.length &&
      [...originalMilestones]
        .sort()
        .every((val, i) => val === [...milestones].sort()[i]);

    const accessUnchanged = editAccess === originalAccess;
    setEditUserModalVisible(false);
    if (milestonesUnchanged && accessUnchanged) {
      toast.info("No changes detected.");
      return;
    }

    try {
      const response = await api.patch(
        getRoute("users", "admin", "users", false, false, "edit"),
        {
          id: selectedUser._id,
          milestones: milestones,
          access: editAccess,
        },
      );

      if (response.status === 200) {
        toast.success("User updated successfully!");
        fetchUsers();
      }
    } catch (error) {
      toast.error("Failed to update user.");
      console.error("Error updating user:", error);
    }
  };

  // Filter milestones based on selected projects
  const getFilteredMilestones = () => {
    if (!editProject.length || !milestoneList.length) return [];
    return milestoneList
      .filter((milestone) => milestone && milestone.project) // remove nulls
      .filter((milestone) => editProject.includes(milestone.project._id));
  };

  const projectCount = user.projects?.length || 0;

  const projectNames = user.projects
    ?.map((p) => p.name)
    .filter(Boolean)
    .join(", ");

  const milestoneNames = user.projects
    ?.flatMap((p) => p.milestones?.map((m) => m.name).filter(Boolean) || [])
    .join(", ");

  return (
    <div className="min-h-screen flex flex-col p-3 gap-2 overflow-hidden  font-customFont">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
        <div style={{ fontFamily: "Noto Serif, sans-serif" }}>
          <h1 className="font-bold text-lg md:text-xl">Team Management</h1>
          <p className="text-xs text-gray-500 font-semibold">
            Manage your team members and their access
          </p>
        </div>
        <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto mt-3 md:mt-0">
          <button
            className="bg-themeGradient text-white hover:bg-themeGradientHover px-3 py-1 rounded-md flex items-center justify-center w-full md:w-auto text-[14px]"
            onClick={toggleModal}
          >
            <i className="fa-solid fa-plus text-lg"></i>
            <span className="ms-2">Add Member</span>
          </button>
        </div>
      </div>

      <div className="flex-shrink-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {/* Total Member Card */}
        <div className="group relative overflow-hidden border border-blue-200 bg-gradient-to-br from-blue-50 via-white to-blue-100 shadow-lg  transition-all duration-300 rounded-xl p-4 pb-3 flex flex-col sm:col-span-2 lg:col-span-1">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200 rounded-full -mr-16 -mt-16 opacity-20"></div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-md">
                <i className="fas fa-file-invoice text-white text-md"></i>
              </div>
              <p className="text-blue-700 text-sm font-semibold uppercase tracking-wide">
                Total Members
              </p>
            </div>
          </div>
          <p className="text-blue-900 text-3xl font-bold mt-1 relative z-10">
            <span>{users.length}</span>
          </p>
          <p className="text-blue-600 text-xs mt-1 font-medium">
            Total team size
          </p>
        </div>

        {/* Active Member Card */}
        <div className="group relative overflow-hidden border border-green-200 bg-gradient-to-br from-green-50 via-white to-green-100 shadow-lg  transition-all duration-300 rounded-xl p-4 pb-3 flex flex-col ">
          <div className="absolute top-0 right-0 w-32 h-32 bg-green-200 rounded-full -mr-16 -mt-16 opacity-20"></div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center shadow-md">
                <i className="fas fa-project-diagram text-white text-md"></i>
              </div>
              <p className="text-green-700 text-sm font-semibold uppercase tracking-wide">
                Active Members
              </p>
            </div>
          </div>
          <p className="text-green-900 text-3xl font-bold mt-2 relative z-10">
            {
              users.filter(
                (user) =>
                  user.status?._id === process.env.REACT_APP_STATUS_ACTIVE,
              ).length
            }
          </p>
          <p className="text-green-600 text-xs mt-1 font-medium">
            Currently active members
          </p>
        </div>
        {/* Inactive Member Card */}
        <div className="group relative overflow-hidden border border-gray-200 bg-gradient-to-br from-gray-50 via-white to-gray-100 shadow-lg transition-all duration-300 rounded-2xl p-4 pb-3 flex flex-col">
          <div className="absolute top-0 right-0 w-32 h-32 bg-gray-200 rounded-full -mr-16 -mt-16 opacity-20"></div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-gray-500 to-gray-700 flex items-center justify-center shadow-md">
                <i className="fas fa-receipt text-white text-md"></i>
              </div>
              <p className="text-gray-700 text-sm font-semibold uppercase tracking-wide">
                Inactive Members
              </p>
            </div>
          </div>
          <p className="text-gray-900 text-3xl font-bold mt-2 relative z-10">
            {
              users.filter(
                (user) =>
                  user.status?._id === process.env.REACT_APP_STATUS_INACTIVE,
              ).length
            }
          </p>
          <p className="text-gray-600 text-xs mt-1 font-medium">
            Currently inactive team members
          </p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:space-x-2">
        {/* Status Filter */}
        <div className="flex flex-col md:w-1/6">
          <CustomSelect
            id="status"
            value={selectedStatus}
            onChange={handleStatusChange}
            useAntd={true}
            options={[
              { value: "All", label: "All Status" },
              { value: process.env.REACT_APP_STATUS_ACTIVE, label: "Active" },
              {
                value: process.env.REACT_APP_STATUS_INACTIVE,
                label: "Inactive",
              },
            ]}
          />
        </div>

        {/* Access Filter */}
        <div className="flex flex-col md:w-1/6">
          <CustomSelect
            name="access"
            value={selectedAccess}
            onChange={handleAccessChange}
            required
            useAntd={true}
            options={[
              { value: "All", label: "All Access" },
              { value: process.env.REACT_APP_ACCESS_VIEW, label: "View" },
              { value: process.env.REACT_APP_ACCESS_EDIT, label: "Edit" },
            ]}
          />
        </div>

        {/* Reset Button */}
        <div className="w-full md:w-auto">
          {" "}
          {/* full width only on mobile */}
          <button
            onClick={handleRefresh}
            className="text-black border border-[#DCE3E3] bg-white px-3 py-2 w-full md:w-auto rounded-md flex items-center justify-center"
          >
            <FontAwesomeIcon icon={faRefresh} />
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center items-center h-48 mt-20">
            <div className="spinner"></div>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => {
                  const projectCount = user.projects?.length || 0;

                  const projectNames = user.projects
                    ?.map((p) => p.name)
                    .filter(Boolean)
                    .join(", ");

                  const milestoneNames = user.projects
                    ?.flatMap(
                      (p) =>
                        p.milestones?.map((m) => m.name).filter(Boolean) || [],
                    )
                    .join(", ");

                  return (
                    <div
                      key={user._id}
                      className="group relative overflow-hidden rounded-2xl backdrop-blur-sm bg-white/80 border border-gray-100 hover:border-gray-300 shadow-md p-6 mb-2 transition-all duration-500"
                    >
                      {/* Header */}
                      <div className="relative flex justify-between items-start mb-6">
                        <div className="flex items-center gap-5">
                          {/* Avatar */}
                          <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl blur-lg opacity-40 group-hover:opacity-60 transition-opacity duration-300"></div>
                            <div className="relative w-16 h-16 bg-gradient-to-br from-blue-50 to-indigo-100 border-2 border-white shadow-lg flex items-center justify-center rounded-2xl transform  transition-transform duration-300">
                              <span className="text-2xl text-blue-700 font-bold">
                                {user.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>

                          {/* User Info */}
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent group-hover:from-blue-700 group-hover:to-indigo-800 transition-all duration-300">
                                {user.name}
                              </h3>
                              <div
                                className={`h-2 w-2 rounded-full ${
                                  user.status?._id ===
                                  process.env.REACT_APP_STATUS_ACTIVE
                                    ? "bg-green-500"
                                    : "bg-gray-400"
                                } transition-colors duration-300`}
                              ></div>
                            </div>
                            <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                              <i className="fa-regular fa-envelope text-gray-400"></i>
                              {user.email}
                            </p>
                          </div>
                        </div>

                        {/* Edit Button */}
                        <img
                          src="../icons/Edit.svg"
                          className="cursor-pointer"
                          onClick={() => handleEditUser(user)}
                          alt="edit"
                        />
                      </div>

                      {/* Divider */}
                      <div className="border-t border-gray-200 my-3" />

                      <div className="flex items-stretch gap-6">
                        {/* Projects Summary */}
                        <div className="flex-1 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100/50 border border-gray-100 p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                              <i className="fa-solid fa-folder text-blue-600"></i>
                            </div>
                            <p className="text-sm font-semibold text-gray-700">
                              Projects ({projectCount})
                            </p>
                          </div>
                          <p className="text-sm text-gray-700 line-clamp-2">
                            {user.projects
                              .map((p) => p?.name)
                              .filter(Boolean)
                              .map((name, index) => (
                                <li key={index}>{name}</li>
                              )) || "No projects assigned"}
                          </p>
                        </div>

                        {/* Milestones */}
                        <div className="flex-1 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100/50 border border-gray-100 p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                              <i className="fa-solid fa-flag text-green-600"></i>
                            </div>
                            <p className="text-sm font-semibold text-gray-700">
                              Milestones
                            </p>
                          </div>
                          <p className="text-sm text-gray-700 line-clamp-2">
                            {user.projects.flatMap(
                              (p) =>
                                p.milestones
                                  ?.map((m) => m?.name)
                                  .filter(Boolean)
                                  .map((name, index) => (
                                    <li key={`${p._id}-${index}`}>{name}</li>
                                  )) || [],
                            ) || "No milestones"}
                          </p>
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="border-t border-gray-200 my-3" />

                      {/* Access & Status */}
                      <div className="flex justify-between items-center">
                        <div
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
                            user.access?._id ===
                            process.env.REACT_APP_ACCESS_EDIT
                              ? "bg-green-50 text-green-700"
                              : "bg-blue-50 text-blue-700"
                          }`}
                        >
                          {/* <i
                            className={`fa-solid ${
                              user.access?._id ===
                              process.env.REACT_APP_ACCESS_EDIT
                                ? 'fa-pen-to-square'
                                : 'fa-eye'
                            } text-xs opacity-75`}
                          ></i> */}
                          <p className="text-sm font-medium capitalize">
                            {user.access?.name || "N/A"}
                          </p>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">Status</span>
                          <div
                            className={`relative inline-block w-12 h-6 transition rounded-full cursor-pointer ${
                              user.status?._id ===
                              process.env.REACT_APP_STATUS_ACTIVE
                                ? "bg-green-500"
                                : "bg-gray-400"
                            }`}
                            onClick={() =>
                              updateStatus(user._id, user.status._id)
                            }
                          >
                            <span
                              className={`absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                                user.status?._id ===
                                process.env.REACT_APP_STATUS_ACTIVE
                                  ? "translate-x-6"
                                  : "translate-x-0"
                              }`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="col-span-full flex flex-col items-center justify-center my-10 bg-white border border-gray-200 text-blue-800 rounded-lg py-5">
                  <i className="fa-solid fa-user-large-slash text-gray-400 text-5xl"></i>
                  <p className="text-gray-400 mt-4">
                    Please create a User to view its details.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {isOpen ? (
        <AddSubUser onSuccess={handleSuccess} onCancel={toggleModal} />
      ) : null}

      <CustomModal
        modalTitle="Edit User Access"
        isOpen={editUserModalVisible}
        onClose={() => setEditUserModalVisible(false)}
      >
        <form onSubmit={handleUpdateUser} className="flex flex-col space-y-4">
          {/* Projects */}
          <div>
            <label className="block font-medium mb-1">Projects</label>
            <Select
              mode="multiple"
              value={editProject}
              onChange={handleEditProjectChange}
              className="w-full"
            >
              {projectList.map((project) => (
                <Option key={project._id} value={project._id}>
                  {project.name}
                </Option>
              ))}
            </Select>
          </div>

          {/* Milestones */}
          <div className="mb-4">
            <label className="block font-medium mb-1">Milestones</label>
            <Select
              mode="multiple"
              value={milestones}
              onChange={handleMilestoneChange}
              className="w-full rounded-lg"
              placeholder={
                editProject.length === 0
                  ? "Please select projects first"
                  : "Select milestones"
              }
              disabled={editProject.length === 0} // Disable if no projects selected
            >
              {getFilteredMilestones().map((mile) => (
                <Option key={mile._id} value={mile._id}>
                  {mile.name} - {mile.project?.name || "N/A"}
                </Option>
              ))}
            </Select>
          </div>

          {/* Access */}
          <div>
            <label className="block font-medium mb-1">Access</label>
            <Select
              value={editAccess}
              onChange={handleEditAccessChange}
              className="w-full"
            >
              <Option value={process.env.REACT_APP_ACCESS_VIEW}>View</Option>
              <Option value={process.env.REACT_APP_ACCESS_EDIT}>Edit</Option>
            </Select>
          </div>
          <ModalFooter
            onCancel={(e) => setEditUserModalVisible(false)}
            isLoading={loading}
          />
        </form>
      </CustomModal>
    </div>
  );
};

export default Team;
